import React, { useEffect, useState } from "react";
import { Handshake, RefreshCw, Mail, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["new", "contacted", "closed", "won", "lost", "all"];

/**
 * SponsorshipAdminTab — admin queue for sponsorship inquiries.
 * Review, mark contacted / won / lost / closed, assign to a rep and
 * keep freeform notes.
 */
export default function SponsorshipAdminTab() {
  const [status, setStatus] = useState("new");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/admin/sponsorship/inquiries?status=${status}`),
        api.get(`/admin/sponsorship/stats`),
      ]);
      setRows(r.data || []); setStats(s.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const patch = async (row, body) => {
    try {
      await api.patch(`/admin/sponsorship/inquiries/${row.id}`, body);
      toast.success("Updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-sponsorship-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <Handshake className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-black text-2xl text-white">Sponsorship inquiries</h2>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
        {["total", "new", "contacted", "won", "lost", "closed"].map((k) => (
          <div key={k} className="bg-white/5 border border-white/10 rounded p-2">
            <div className="text-[9px] uppercase text-white/50 font-black">{k}</div>
            <div className="font-display font-black text-xl">{stats[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`sponsor-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded ${status === s ? "bg-tv-accent text-black" : "bg-white/5 text-white/70"}`}
            >{s}</button>
          ))}
        </div>
        <button onClick={load} className="ml-auto text-white/60 hover:text-white text-sm inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2" data-testid="admin-sponsorship-list">
          {rows.length === 0 && <div className="text-white/40 text-sm text-center py-6">No {status} inquiries.</div>}
          {rows.map((r) => {
            const draft = drafts[r.id] || { notes: r.notes || "", assigned_to: r.assigned_to || "" };
            return (
              <div key={r.id} data-testid={`sponsor-inq-${r.id}`} className="bg-white/5 border border-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-black text-lg flex-1 truncate">{r.company}</div>
                  <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${
                    r.status === "new" ? "bg-amber-500/25 text-amber-200" :
                    r.status === "contacted" ? "bg-sky-500/25 text-sky-200" :
                    r.status === "won" ? "bg-emerald-500/25 text-emerald-200" :
                    r.status === "lost" ? "bg-red-500/25 text-red-200" :
                    "bg-white/10 text-white/60"
                  }`}>{r.status}</span>
                </div>
                <div className="text-white/70 text-sm mt-0.5 flex items-center gap-3 flex-wrap">
                  <span>{r.contact_name}</span>
                  <a className="text-tv-accent hover:underline inline-flex items-center gap-1" href={`mailto:${r.contact_email}`}><Mail className="w-3 h-3" /> {r.contact_email}</a>
                  {r.contact_phone && <span>· {r.contact_phone}</span>}
                  {r.website && <a className="text-tv-accent hover:underline inline-flex items-center gap-1" href={r.website} target="_blank" rel="noreferrer"><Globe className="w-3 h-3" /> site <ExternalLink className="w-3 h-3" /></a>}
                  <span className="text-white/50">budget <strong>{r.budget_tier}</strong></span>
                </div>
                {r.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {r.interests.map((i) => <span key={i} className="text-[10px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded">{i}</span>)}
                  </div>
                )}
                {r.message && <p className="text-white text-sm mt-2 whitespace-pre-wrap">{r.message}</p>}
                <div className="text-[10px] text-white/40 mt-1.5">received {new Date(r.created_at).toLocaleString()}</div>

                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <input
                    data-testid={`sponsor-assign-${r.id}`}
                    placeholder="Assigned to (rep name)"
                    value={draft.assigned_to}
                    onChange={(e) => setDrafts({ ...drafts, [r.id]: { ...draft, assigned_to: e.target.value } })}
                    className="w-48 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white"
                  />
                  <input
                    data-testid={`sponsor-notes-${r.id}`}
                    placeholder="Internal notes"
                    value={draft.notes}
                    onChange={(e) => setDrafts({ ...drafts, [r.id]: { ...draft, notes: e.target.value } })}
                    className="flex-1 min-w-[180px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white"
                  />
                  <button
                    data-testid={`sponsor-save-${r.id}`}
                    onClick={() => patch(r, draft)}
                    className="bg-white/10 hover:bg-white/20 text-white font-black text-xs px-3 py-1.5 rounded"
                  >Save</button>
                  <button data-testid={`sponsor-contacted-${r.id}`} onClick={() => patch(r, { status: "contacted" })} className="bg-sky-500 hover:bg-sky-400 text-black font-black text-xs px-3 py-1.5 rounded">Contacted</button>
                  <button data-testid={`sponsor-won-${r.id}`} onClick={() => patch(r, { status: "won" })} className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded">Won</button>
                  <button data-testid={`sponsor-lost-${r.id}`} onClick={() => patch(r, { status: "lost" })} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded">Lost</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
